import dayjs from 'dayjs';
import useFilters from './useFilters';

export type DateRangeOption =
  | 'LAST_30_DAYS_FROM_TODAY'
  | 'LAST_30_DAYS_FROM_YESTERDAY'
  | 'LAST_MONTH'
  | 'LAST_6_MONTHS';

export const getDefaultDateRange = (option: DateRangeOption) => {
  const today = dayjs();
  const yesterday = dayjs().subtract(1, 'day');

  switch (option) {
    case 'LAST_30_DAYS_FROM_TODAY':
      return {
        start_date: today
          .subtract(30, 'day')
          .startOf('day')
          .format('YYYY-MM-DD'),
        end_date: today.endOf('day').format('YYYY-MM-DD'),
      };

    case 'LAST_30_DAYS_FROM_YESTERDAY':
      return {
        start_date: yesterday
          .subtract(30, 'day')
          .startOf('day')
          .format('YYYY-MM-DD'),
        end_date: yesterday.endOf('day').format('YYYY-MM-DD'),
      };

    case 'LAST_MONTH': {
      const lastMonth = dayjs().subtract(1, 'month');
      return {
        start_date: lastMonth.startOf('month').format('YYYY-MM-DD'),
        end_date: lastMonth.endOf('month').format('YYYY-MM-DD'),
      };
    }
    case 'LAST_6_MONTHS': {
      const endOfLastMonth = dayjs()
        .startOf('month')
        .subtract(1, 'day')
        .endOf('day');

      const startOfSixMonthsAgo = endOfLastMonth
        .subtract(5, 'month')
        .startOf('month');

      return {
        start_date: startOfSixMonthsAgo.format('YYYY-MM-DD'),
        end_date: endOfLastMonth.format('YYYY-MM-DD'),
      };
    }

    default:
      return {};
  }
};

type DateRangeFilter = {
  start_date?: string;
  end_date?: string;
};

export type ManagementFilterKey =
  | 'netOfficeHour'
  | 'graceTimeAbuse'
  | 'workDiscipline'
  | 'disciplineIssue'
  | 'graceTimeAbuseIndex'
  | 'costPerProductiveHour'
  | 'payrollLeakageLostWorkHour'
  | 'highSalaryLowOutput'
  | 'attendanceHealth'
  | 'workForceCulture'
  | 'disciplineTrend';

export type ManagementFilters = {
  department_id?: string;
  office_id?: string;
} & Record<ManagementFilterKey, DateRangeFilter>;

const LAST_MONTH = getDefaultDateRange('LAST_MONTH');
const LAST_6_MONTHS = getDefaultDateRange('LAST_6_MONTHS');
const L_30_D_F_YESTERDAY = getDefaultDateRange('LAST_30_DAYS_FROM_YESTERDAY');

const DEFAULT_FILTERS: ManagementFilters = {
  department_id: undefined,
  office_id: undefined,
  netOfficeHour: L_30_D_F_YESTERDAY,
  graceTimeAbuse: L_30_D_F_YESTERDAY,
  workDiscipline: L_30_D_F_YESTERDAY,
  disciplineIssue: L_30_D_F_YESTERDAY,
  graceTimeAbuseIndex: L_30_D_F_YESTERDAY,
  costPerProductiveHour: LAST_6_MONTHS,
  payrollLeakageLostWorkHour: L_30_D_F_YESTERDAY,
  highSalaryLowOutput: LAST_MONTH,
  attendanceHealth: {},
  workForceCulture: L_30_D_F_YESTERDAY,
  disciplineTrend: LAST_6_MONTHS,
};

const useManagementFilters = () => {
  const { filter, handleFilter } = useFilters<ManagementFilters>({
    defaultFilter: DEFAULT_FILTERS,
  });

  /* ---- setters ---- */

  const setFilter = (
    key: ManagementFilterKey | 'department_id' | 'office_id',
    value: any,
  ) => {
    handleFilter(key as any, value);
  };

  const setFilterByRange = (
    key: ManagementFilterKey,
    range: DateRangeOption,
  ) => {
    handleFilter(key, getDefaultDateRange(range));
  };

  /* ---- getters ---- */

  const getFilter = (key: ManagementFilterKey) => filter[key];

  /* ---- reset helpers ---- */

  const resetFilter = (key: ManagementFilterKey) => {
    handleFilter(key, DEFAULT_FILTERS[key]);
  };

  const resetAllFilters = () => {
    const keys = Object.keys(DEFAULT_FILTERS).filter(
      (k) => k !== 'department_id' && k !== 'office_id',
    ) as ManagementFilterKey[];

    keys.forEach((key) => handleFilter(key, DEFAULT_FILTERS[key]));
  };

  const isFilterChanged = (key: ManagementFilterKey) => {
    const current = filter[key];
    const initial = DEFAULT_FILTERS[key];
    return JSON.stringify(current) !== JSON.stringify(initial);
  };

  return {
    filter,
    getFilter,
    isFilterChanged,

    setFilter,
    setFilterByRange,

    resetFilter,
    resetAllFilters,
  };
};

export default useManagementFilters;
